/* eslint-disable prettier/prettier */
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Row, Col } from 'react-bootstrap';
import { parseISO, format, startOfDay } from 'date-fns';
import { usePerfil } from '../../../hooks/perfil';
import { useClinica } from '../../../hooks/clinica';
import { useToast } from '../../../hooks/toast';
import { User } from '../../../hooks/auth';

import Loading from '../../../components/Loading';
import InputFake from '../../../components/InputFake';
import SelectSync, { IOption } from '../../../components/SelectSync';
import TextArea from '../../../components/TextArea';
import getValidationErrors from '../../../utils/getValidationErrors';
import Toggle from '../../../components/Exame/Toggle';
import api from '../../../services/api';
import { useMedico } from '../../../hooks/medico';
import DiasHorarios from './DiasHorarios';
import Quinzenal from './Quinzenal';
import { optionsDuracao } from '../../../utils/funcoes';

import { IDadosLista } from '../ListaAgendas';
import getResponseErrors from '../../../utils/getResponseErrors';
import DiasNaoAgendarCadastro from './DiasNaoAgendar/DiaNaoAgendarCadastro';
import ParceiroAgendaCadastro from './Parceiros/ParceiroAgendaCadastro';

interface IHorario {
  HorCodigo?: number;
  HorConCodigo?: number;
  HorIntervaloIni: string;
  HorIntervaloFim: string;
  HorHoraIni: string;
  HorHoraFim: string;
  HorMaxAgendamento?: number;
}

interface IHorarioRecorrente extends IHorario {
  HorDataIni: Date | string;
  HorDiasRecorre: number | string;
}

interface IHorarios extends IHorario {
  HorDia: number | string;
  selecionado?: boolean;
}

export interface IConfiguracaoAgenda {
  ConCodigo: number;
  PerCodigo?: number[];
  codsPerfil?: string[];
  ConPerfil: string;
  // perfil: IPerfil;
  ConUsuCodigo?: number;
  medico: User;
  ConNome: string;
  ConTempoPadrao: number;
  ConObs: string;
  ConAtende: boolean;
  ConEnviaEmail: boolean;
  ConDiasEmail: number;
  ConClinica?: number;
  ConSemanal: number;
  ConIntegracao: number;
  ConPagamentoAntecipado: boolean;
  ConMedicoAgendaCompleta: boolean;
  ConOrdenaPorPagamento: boolean;
  ConAtivo: boolean;
  ConOnline?: boolean;
  ConTratamento: boolean;
  created_at: Date;
  updated_at: Date;
  horarios?: IHorarios[];
  horarioRecorrente?: IHorarioRecorrente;
}

type Props = {
  conCodigo?: number;
  labels?: IDadosLista;
  addAgenda(
    dadosAgenda: IConfiguracaoAgenda,
    dadosLista: IDadosLista,
    ageCodigo?: number,
  ): void;
};

const AgendasCadastroPagina: React.FC<Props> = ({
  conCodigo,
  labels,
  addAgenda,
}) => {
  const { optionsPerfil, buscaPerfil } = usePerfil();
  const { optionsClinica, imagensClinica } = useClinica();
  const { optionsMedico, buscaOptionsMedico } = useMedico();
  const { addToast } = useToast();

  const [tipoAgendaSemanal, setTipoAgendaSemanal] = useState(true);
  const formRefConfiguracaoAgenda = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [optionsPerfis, _] = useState<IOption[]>(optionsPerfil.optionPerfis);
  const [configuracaoAgenda, setConfiguracaoAgenda] = useState<
    IConfiguracaoAgenda | undefined
  >(undefined);

  const cliSaoPaulo = !!imagensClinica.find(img => img.nome === 'saopaulo');

  const optionsOrdenacao = [
    {
      label: 'Horário Agendado',
      value: 'false',
    },
    {
      label: 'Chegada / Pagamento',
      value: 'true',
    },
  ];

  const optionsVisualizacaoMedico = [
    {
      label: 'Vê todos agendamentos',
      value: 'true',
    },
    {
      label: 'Somente lista espera',
      value: 'false',
    },
  ];

  function recuperaHorarios(horarios: IHorarios[] | undefined): IHorarios[] {
    const dias = [1, 2, 3, 4, 5, 6, 7];
    const horariosFormatado = dias.map(dia => {
      const horariosDia = {
        HorDia: dia.toString(),
        HorHoraFim: '',
        HorHoraIni: '',
        HorIntervaloFim: '',
        HorIntervaloIni: '',
        selecionado: false,
        HorMaxAgendamento: 0,
      };
      let buscaHorario = horarios?.find(horario => horario.HorDia === dia);
      if (buscaHorario)
        buscaHorario = {
          ...buscaHorario,
          selecionado: true,
        };
      return { ...horariosDia, ...buscaHorario };
    });
    return horariosFormatado;
  }

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!optionsPerfil || Object.keys(optionsPerfil).length === 0) loadPerfis();
  }, [buscaPerfil, optionsPerfil]);

  useEffect(() => {
    async function loadMedicos(): Promise<void> {
      await buscaOptionsMedico();
    }
    if (!optionsMedico || Object.keys(optionsMedico).length === 0)
      loadMedicos();
  }, [buscaOptionsMedico, optionsMedico]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IConfiguracaoAgenda>(
          `agendas/${conCodigo}/detalhe`,
        );

        const horarios = recuperaHorarios(response.data.horarios);
        const horarioRecorrente = response.data.horarioRecorrente && {
          ...response.data.horarioRecorrente,
          HorDataIni: format(
            parseISO(response.data.horarioRecorrente.HorDataIni.toString()),
            'yyyy-MM-dd',
          ),
          HorDiasRecorre: response.data.horarioRecorrente.HorDiasRecorre.toString(),
        };

        setConfiguracaoAgenda({
          ...response.data,
          horarios,
          horarioRecorrente,
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dados da agenda',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (conCodigo) loadDados();
  }, [conCodigo, addToast]);

  useEffect(() => {
    if (configuracaoAgenda) {
      setTipoAgendaSemanal(configuracaoAgenda.ConSemanal === 1);
    }
  }, [configuracaoAgenda]);

  const ehNovo = useMemo(() => {
    return !conCodigo || conCodigo === 0;
  }, [conCodigo]);

  const handleSubmitConfiguracao = useCallback(
    async (dados: IConfiguracaoAgenda) => {
      try {
        setLoading(true);
        let adic = {};
        const validaHorarios = {
          horarios: Yup.array().of(
            Yup.object().shape({
              selecionado: Yup.boolean(),
              HorIntervaloIni: Yup.string(),
              HorIntervaloFim: Yup.string(),
              HorHoraIni: Yup.string().when('selecionado', {
                is: true,
                then: Yup.string().required('informe a hora inicial'),
              }),
              HorHoraFim: Yup.string().when('selecionado', {
                is: true,
                then: Yup.string().required('informe a hora final'),
              }),
              HorMaxAgendamento: Yup.string().when('selecionado', {
                is: true,
                then: Yup.string()
                  .min(1, 'selecione o número máximo de agendamentos')
                  .required('informe o número máximo de agendamentos'),
              }),
            }),
          ),
        };
        const validaHorarioRecorrente = {
          horarioRecorrente: Yup.object().shape({
            HorIntervaloIni: Yup.string(),
            HorIntervaloFim: Yup.string(),
            HorHoraIni: Yup.string().required('informe a hora inicial'),
            HorHoraFim: Yup.string().required('informe a hora final'),
            HorDataIni: Yup.date().required('informe a data inicial'),
            HorDiasRecorre: Yup.number().required(),
            HorMaxAgendamento: Yup.number().required(
              'informe o número máximo de agendamentos',
            ),
          }),
        };
        if (tipoAgendaSemanal) adic = { ...adic, ...validaHorarios };
        if (!tipoAgendaSemanal) adic = { ...adic, ...validaHorarioRecorrente };

        const cods = `${dados.codsPerfil}`;

        // console.log(cods, dados.codsPerfil);
        let perfil = '';
        if (ehNovo) {
          if (!dados.codsPerfil) {
            formRefConfiguracaoAgenda.current?.setFieldError(
              'codsPerfil',
              'Selecione pelo menos 1 perfil',
            );
            addToast({
              title: 'Nenhum perfil selecionado',
              type: 'error',
              description: 'Selecione pelo menos 1 perfil',
            });
            return;
          }
          perfil = dados.codsPerfil
            .map(
              cod =>
                optionsPerfis.find(per => per.value === cod.toString())?.label,
            )
            .join(' / ');
        }

        formRefConfiguracaoAgenda.current?.setErrors({});
        const schema = Yup.object().shape({
          // ...(ehNovo ? { ConPerCodigo: Yup.number().required() } : {}),
          ...(ehNovo ? { ConUsuCodigo: Yup.number().required() } : {}),
          ...(ehNovo ? { ConClinica: Yup.number().required() } : {}),
          ConTempoPadrao: Yup.number().required(),
          ConObs: Yup.string(),
          ConDiasEmail: Yup.number().required(),
          // ConSemanal: Yup.number().required(),
          // ConIntegracao: Yup.number().required(),
          // ConMaxAgendamento: Yup.number().required(),
          ...adic,
        });

        const pagamentoAntecipado = dados.ConPagamentoAntecipado
          ? dados.ConPagamentoAntecipado.toString()
          : 'false';
        const medicoAgendaCompleta = dados.ConMedicoAgendaCompleta
          ? dados.ConMedicoAgendaCompleta.toString()
          : 'false';
        const ordenaPorPagamento = dados.ConOrdenaPorPagamento
          ? dados.ConOrdenaPorPagamento.toString()
          : 'false';
        const ativo = dados.ConAtivo ? dados.ConAtivo.toString() : 'false';
        const tratamento = dados.ConTratamento
          ? dados.ConTratamento.toString()
          : 'false';
        const atende = dados.ConAtende ? dados.ConAtende.toString() : 'false';
        const enviaEmail = dados.ConEnviaEmail
          ? dados.ConEnviaEmail.toString()
          : 'false';

        const integracao =
          dados.ConIntegracao?.toString() === 'true' ||
            dados.ConIntegracao === 1 ||
            dados.ConIntegracao?.toString() === '1'
            ? 'true'
            : 'false';

        const configuracaoForm = {
          ...dados,
          ConObs: dados.ConObs.toUpperCase(),
          ConEnviaEmail: enviaEmail === 'true',
          ConPagamentoAntecipado: pagamentoAntecipado === 'true',
          ConMedicoAgendaCompleta: medicoAgendaCompleta === 'true',
          ConOrdenaPorPagamento: ordenaPorPagamento === 'true',
          ConAtivo: ativo === 'true',
          ConTratamento: tratamento === 'true',
          ConAtende: atende === 'true',
          ConIntegracao: integracao === 'true' ? 1 : 0,
          ...(ehNovo && {
            PerCodigo: cods
              .split(',')
              .filter(it => it !== '')
              .map(item => Number(item)),
          }),
        };

        await schema.validate(configuracaoForm, {
          abortEarly: false,
        });

        const medico = optionsMedico.find(
          med => med.value === configuracaoForm.ConUsuCodigo?.toString(),
        );

        const clinica = optionsClinica.find(
          cli => cli.value === configuracaoForm.ConClinica?.toString(),
        );

        const horarios = configuracaoForm.horarios
          ?.filter(
            (hor: IHorarios) =>
              hor.selecionado && hor.selecionado.toString() === 'true',
          )
          .map(
            (hor: IHorarios): IHorarios => ({
              HorDia: hor.HorDia,
              HorHoraFim: hor.HorHoraFim,
              HorHoraIni: hor.HorHoraIni,
              HorIntervaloFim: hor.HorIntervaloFim,
              HorIntervaloIni: hor.HorIntervaloIni,
              HorMaxAgendamento: hor.HorMaxAgendamento,
            }),
          );

        const horarioRecorrenteFormatado = configuracaoForm.horarioRecorrente && {
          ...configuracaoForm.horarioRecorrente,
          HorDataIni: startOfDay(
            parseISO(configuracaoForm.horarioRecorrente.HorDataIni.toString()),
          ),
        };

        const dadosEnvio: IConfiguracaoAgenda = {
          ...configuracaoForm,
          horarios,
          horarioRecorrente: horarioRecorrenteFormatado,
          ConSemanal: tipoAgendaSemanal ? 1 : 0,
          ConUsuCodigo: ehNovo ? configuracaoForm.ConUsuCodigo : undefined,
          ConClinica: ehNovo ? configuracaoForm.ConClinica : undefined,
        };

        delete dadosEnvio.codsPerfil;

        if (ehNovo) {
          await addAgenda(dadosEnvio, {
            perfil,
            medico: medico?.label,
            clinica: clinica?.label,
          });
        } else {
          await addAgenda(
            dadosEnvio,
            {
              perfil: labels?.perfil,
              medico: labels?.medico,
              clinica: labels?.clinica,
            },
            conCodigo,
          );
        }
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefConfiguracaoAgenda.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Erro ao salvar procedimento',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      addAgenda,
      ehNovo,
      optionsClinica,
      optionsMedico,
      conCodigo,
      optionsPerfis,
      tipoAgendaSemanal,
      labels,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => { }, []);

  const diasAviso = useMemo((): IOption[] => {
    const horas = [];
    // eslint-disable-next-line no-plusplus
    for (let h = 1; h < 7; h++) {
      horas.push({ label: `${h}`, value: `${h}` });
    }
    return horas;
  }, []);

  return (
    <div className="container">
      {loading && <Loading />}

      <Row noGutters>
        <Col md={12}>
          {(ehNovo || configuracaoAgenda) && (
            <>
              <Form
                ref={formRefConfiguracaoAgenda}
                initialData={{
                  ...configuracaoAgenda,
                  ConUsuCodigo: configuracaoAgenda?.ConUsuCodigo?.toString(),
                  // ConPerCodigo: configuracaoAgenda?.ConPerCodigo?.toString(),
                  ConClinica: configuracaoAgenda?.ConClinica?.toString(),
                  ConTempoPadrao: configuracaoAgenda?.ConTempoPadrao.toString(),
                  ConMedicoAgendaCompleta: configuracaoAgenda?.ConMedicoAgendaCompleta.toString(),
                  ConOrdenaPorPagamento: configuracaoAgenda?.ConOrdenaPorPagamento.toString(),
                  ConDiasEmail: configuracaoAgenda?.ConDiasEmail.toString(),
                  horarios: configuracaoAgenda
                    ? configuracaoAgenda.horarios
                    : recuperaHorarios(undefined),
                }}
                onSubmit={handleSubmitConfiguracao}
              >
                <Row>
                  <Col md={12} className="form-group pr-2">
                    {ehNovo ? (
                      <SelectSync
                        name="codsPerfil"
                        label="Perfil"
                        placeholder="Selecione"
                        handleSelectChange={handleOnChangeVazio}
                        isMulti
                        options={optionsPerfis}
                        isDisabled={!ehNovo}
                        size="gr"
                      />
                    ) : (
                      <InputFake
                        label="Perfil"
                        value={configuracaoAgenda?.ConPerfil}
                        disabled
                      />
                    )}
                  </Col>

                  <Col md={6} className="form-group pr-2">
                    <SelectSync
                      name="ConUsuCodigo"
                      label="Médico"
                      placeholder="Selecione"
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsMedico}
                      isDisabled={!ehNovo}
                    />
                  </Col>
                  <Col md={6} className="form-group pr-2">
                    <SelectSync
                      name="ConClinica"
                      label="Clínica"
                      placeholder="Selecione"
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsClinica}
                      isDisabled={!ehNovo}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      type="button"
                      variant={tipoAgendaSemanal ? 'info' : 'outline-info'}
                      className="mt-2 mr-2"
                      onClick={() => setTipoAgendaSemanal(true)}
                    >
                      Agenda Semanal
                    </Button>

                    <Button
                      type="button"
                      variant={tipoAgendaSemanal ? 'outline-info' : 'info'}
                      className="mt-2"
                      onClick={() => setTipoAgendaSemanal(false)}
                    >
                      Agenda Recorrente
                    </Button>
                  </Col>
                </Row>
                <div className="card" style={{ padding: '0 16px' }}>
                  {tipoAgendaSemanal && <DiasHorarios />}
                  {!tipoAgendaSemanal && <Quinzenal />}
                </div>
                <Row className="pt-2">
                  <Col md={12} lg={12} className="form-group pr-2">
                    <TextArea name="ConObs" label="Observação" rows={3} />
                  </Col>
                </Row>
                <Row className="pt-2">
                  <Col md={2} lg={2} className="form-group pr-2">
                    <SelectSync
                      name="ConTempoPadrao"
                      label="Tempo Agendamento"
                      placeholder="Selecione"
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsDuracao}
                    />
                  </Col>
                  <Col
                    md={3}
                    lg={3}
                    className="form-group pr-2"
                    style={{ display: 'grid', textAlign: 'center' }}
                  >
                    <Toggle
                      nome="ConAtende"
                      classSpam={{
                        paddingRight: '10px',
                        marginBottom: '7px',
                        fontWeight: 'bold',
                      }}
                      label="Atende Consultório"
                      isDisabled={false}
                    />
                  </Col>

                  <Col
                    md={3}
                    className="form-group pr-2"
                    style={{ display: 'grid', textAlign: 'center' }}
                  >
                    <Toggle
                      nome="ConEnviaEmail"
                      classSpam={{
                        paddingRight: '10px',
                        marginBottom: '7px',
                        fontWeight: 'bold',
                      }}
                      label="Confirmação Auto."
                      isDisabled={false}
                    />
                  </Col>
                  <Col md={2} className="form-group pr-2">
                    <SelectSync
                      name="ConDiasEmail"
                      label="Dias antes Consulta"
                      placeholder="dias"
                      handleSelectChange={handleOnChangeVazio}
                      options={diasAviso}
                      containerStyle={{ maxWidth: '140px' }}
                    />
                  </Col>
                  <Col
                    md={2}
                    className="form-group pr-2"
                    style={{ display: 'grid', textAlign: 'center' }}
                  >
                    <Toggle
                      nome="ConAtivo"
                      label="Ativo"
                      isDisabled={false}
                      classSpam={{
                        paddingRight: '10px',
                        marginBottom: '7px',
                        fontWeight: 'bold',
                      }}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col
                    md={3}
                    className="form-group pr-2"
                    style={{ display: 'grid', textAlign: 'center' }}
                  >
                    <Toggle
                      nome="ConIntegracao"
                      label="Integração Alvaro"
                      isDisabled={false}
                      classSpam={{
                        paddingRight: '10px',
                        marginBottom: '7px',
                        fontWeight: 'bold',
                      }}
                    />
                  </Col>
                  <Col
                    md={3}
                    className="form-group pr-2"
                    style={{ display: 'grid', textAlign: 'center' }}
                  >
                    <Toggle
                      nome="ConPagamentoAntecipado"
                      label="Permite Pagamento Antecipado?"
                      isDisabled={false}
                      classSpam={{
                        paddingRight: '10px',
                        marginBottom: '7px',
                        fontWeight: 'bold',
                      }}
                    />
                  </Col>
                  <Col md={3} className="form-group pr-2">
                    <SelectSync
                      name="ConMedicoAgendaCompleta"
                      label="Visualização dos Agendamentos Médico"
                      placeholder="Selecione"
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsVisualizacaoMedico}
                    />
                  </Col>
                  <Col md={3} className="form-group pr-2">
                    <SelectSync
                      name="ConOrdenaPorPagamento"
                      label="Ordenação Atendimento Médico Por"
                      placeholder="Selecione"
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsOrdenacao}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col
                    md={3}
                    className="form-group pr-2"
                    style={{ display: 'grid', textAlign: 'center' }}
                  >
                    <Toggle
                      nome="ConTratamento"
                      label="Atende Tratamento"
                      isDisabled={false}
                      classSpam={{
                        paddingRight: '10px',
                        marginBottom: '7px',
                        fontWeight: 'bold',
                      }}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="text-right align-items-center p-1">
                      <>
                        <Button type="submit" variant="success">
                          Salvar dados
                        </Button>
                      </>
                    </div>
                  </Col>
                </Row>
              </Form>
              {!ehNovo && conCodigo && (
                <>
                  <DiasNaoAgendarCadastro conCodigo={conCodigo} />
                  {cliSaoPaulo && (
                    <ParceiroAgendaCadastro conCodigo={conCodigo} />
                  )}
                </>
              )}
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AgendasCadastroPagina;
